import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle, Compass, FileText, Globe2, Layers, MapPin, ShieldCheck, Sparkles } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

export default function LandingPage() {
  const { user } = useAuth();

  const steps = [
    { title: 'Define your search direction', text: 'Tell us your target roles, level, markets, and constraints.' },
    { title: 'Find better-fit roles', text: 'Your agent helps focus the search on roles that match your direction.' },
    { title: 'Prepare your application kit', text: 'Get support for a tailored CV and cover letter for selected roles.' },
  ];

  const learns = [
    'Role families',
    'Target titles',
    'Seniority',
    'Markets',
    'Skills',
    'Constraints',
    'CV evidence',
    'Match signals',
  ];

  const markets = ['Germany', 'London/UK', 'Remote Europe', 'Netherlands'];

  const kitItems = [
    'Role-specific CV direction',
    'Tailored CV support',
    'Cover-letter support',
    'Application notes in one place',
  ];

  return (
    <div className="min-h-screen bg-[#f6efe3] text-[#22362f]">
      <section className="relative overflow-hidden pt-28 pb-16 md:pt-36 md:pb-24" data-testid="hero-section">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_18%_10%,rgba(218,142,75,0.16),transparent_34%),linear-gradient(135deg,rgba(255,255,255,0.62),rgba(247,232,210,0.54))]" />
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="relative grid grid-cols-1 lg:grid-cols-[1.05fr_0.95fr] gap-10 lg:gap-16 items-center">
            <div className="animate-fade-in-up">
              <span className="inline-flex items-center gap-2 rounded-full border border-[#d9c8aa] bg-white/45 px-4 py-2 text-xs uppercase tracking-[0.18em] text-[#6c5d48] mb-6" data-testid="hero-eyebrow">
                <Sparkles className="w-3.5 h-3.5" /> Europe-first application workspace
              </span>
              <h1 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl tracking-tight font-medium leading-[1.05] mb-6 text-[#172820]" data-testid="hero-heading">
                Your job-search workspace for Europe
              </h1>
              <p className="text-[#4b5a50] text-base md:text-lg mb-5 max-w-xl leading-relaxed" data-testid="hero-message">
                Complete your job-search profile, review matched roles, and prepare a customised application kit for the roles you care about.
              </p>
              <p className="text-[#6f6657] text-sm md:text-base mb-8 max-w-xl leading-relaxed" data-testid="hero-supporting-copy">
                JobMatchPro helps you define your direction, choose target roles, and prepare role-specific CV and cover-letter materials without turning job search into a spreadsheet.
              </p>
              <div className="flex flex-wrap gap-2 mb-8" data-testid="hero-market-pills">
                {markets.slice(0, 3).map((market) => (
                  <span key={market} className="rounded-full border border-[#d7c5a7] bg-[#fffaf1]/75 px-4 py-2 text-sm text-[#4b5a50]">
                    {market}
                  </span>
                ))}
              </div>
              <div className="flex flex-wrap items-center gap-3 mb-6">
                <Link
                  to="/find-jobs"
                  className="inline-flex items-center justify-center gap-2.5 rounded-md bg-[#173d35] px-6 py-4 text-sm font-medium text-[#fff8ed] shadow-sm transition-[transform,background-color] duration-300 hover:-translate-y-0.5 hover:bg-[#102f29]"
                  data-testid="hero-search-jobs-btn"
                >
                    Start job search
                  <ArrowRight className="w-4 h-4" />
                </Link>
                {user && (
                  <Link
                    to="/dashboard"
                    className="inline-flex items-center justify-center gap-2 rounded-md border border-[#c8b994] bg-white/35 px-5 py-4 text-sm font-medium text-[#173d35] transition-[transform,background-color] duration-300 hover:-translate-y-0.5 hover:bg-white/65"
                    data-testid="hero-dashboard-btn"
                  >
                    View Dashboard
                  </Link>
                )}
              </div>
              <p className="text-xs text-[#7a6d5c] max-w-sm leading-relaxed" data-testid="hero-footnote">
                Built for candidates who want clearer direction, better-fit roles, and stronger preparation.
              </p>
            </div>

            <div className="animate-fade-in-up" style={{ animationDelay: '0.12s' }} data-testid="career-agent-visual-card">
              <div className="rounded-[2rem] border border-[#294d42]/20 bg-[#173d35] p-6 md:p-8 text-[#fff8ed] shadow-2xl shadow-[#6a4d2e]/10">
                <div className="flex items-center justify-between gap-4 mb-8">
                  <div>
                    <p className="text-xs uppercase tracking-[0.22em] text-[#c6d8cb] mb-2">Search profile</p>
                    <h2 className="font-serif-display text-2xl md:text-3xl font-medium">Your direction</h2>
                  </div>
                  <div className="h-12 w-12 rounded-full border border-[#d7eadc]/30 bg-[#244f45] flex items-center justify-center">
                    <Compass className="w-5 h-5" />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3 mb-8">
                  {learns.slice(0, 6).map((item) => (
                    <div key={item} className="rounded-xl border border-[#d7eadc]/14 bg-[#244f45]/70 px-4 py-3 text-sm">
                      {item}
                    </div>
                  ))}
                </div>
                <div className="rounded-2xl bg-[#fff8ed] p-4 text-[#173d35]">
                  <div className="flex items-center gap-3 mb-3">
                    <CheckCircle className="w-5 h-5 text-[#2f7665]" />
                    <span className="text-sm font-medium">Direction saved once</span>
                  </div>
                  <p className="text-sm text-[#53665a] leading-relaxed">
                    Use one profile to guide search focus and prepare selected application materials.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

      </section>

      <section id="features" className="py-14 md:py-20" data-testid="how-it-works-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="mb-10 max-w-xl">
            <h2 className="font-serif-display text-3xl md:text-4xl tracking-tight font-medium mb-3 text-[#172820]">
              How it works
            </h2>
            <p className="text-[#6f6657] text-sm md:text-base">
              A simple path from career direction to selected application materials.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {steps.map((step, index) => (
              <div
                key={step.title}
                className="rounded-2xl border border-[#d8c8ab] bg-[#fff9ee]/70 p-6 shadow-sm transition-[transform,background-color] duration-300 hover:-translate-y-1 hover:bg-[#fffdf7]"
                data-testid={`how-it-works-step-${index}`}
              >
                <span className="mb-5 flex h-10 w-10 items-center justify-center rounded-full bg-[#173d35] text-sm font-medium text-[#fff8ed]">{index + 1}</span>
                <h3 className="font-serif-display text-xl font-medium text-[#172820] mb-2">{step.title}</h3>
                <p className="text-sm leading-relaxed text-[#667466]">{step.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-14 md:py-20" data-testid="career-agent-learns-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-[0.85fr_1.15fr] gap-8 lg:gap-12 items-start">
            <div>
              <h2 className="font-serif-display text-3xl md:text-4xl tracking-tight font-medium mb-4 text-[#172820]">
                What your profile captures
              </h2>
              <p className="text-[#6f6657] text-sm md:text-base leading-relaxed max-w-md">
                Your profile captures the signals that make a role worth your attention, not just a keyword list.
              </p>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="career-agent-learns-grid">
              {learns.map((item) => (
                <div key={item} className="rounded-2xl border border-[#d8c8ab] bg-white/45 p-4 text-sm font-medium text-[#30473e]">
                  {item}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="py-14 md:py-20" data-testid="application-kit-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="rounded-[2rem] border border-[#d8c8ab] bg-[#fff9ee]/80 p-6 md:p-10 shadow-sm">
            <div className="grid grid-cols-1 lg:grid-cols-[0.9fr_1.1fr] gap-8 lg:gap-12 items-center">
              <div>
                <span className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.18em] text-[#7b694d] mb-4">
                  <FileText className="w-4 h-4" /> Customised application kit
                </span>
                <h2 className="font-serif-display text-3xl md:text-4xl tracking-tight font-medium mb-4 text-[#172820]">
                  Prepare stronger role-specific materials
                </h2>
                <p className="text-[#6f6657] text-sm md:text-base leading-relaxed">
                  For selected roles, JobMatchPro supports tailored CV preparation and cover-letter materials so every application can stay connected to the job you care about.
                </p>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {kitItems.map((item) => (
                  <div key={item} className="rounded-2xl bg-[#173d35] p-5 text-[#fff8ed]">
                    <CheckCircle className="w-5 h-5 mb-4 text-[#b9d8c3]" />
                    <p className="text-sm font-medium">{item}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-14 md:py-20" data-testid="free-for-candidates-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="rounded-[2rem] bg-[#173d35] p-8 md:p-10 text-[#fff8ed]" data-testid="free-card">
              <ShieldCheck className="w-8 h-8 mb-6 text-[#b9d8c3]" />
              <h2 className="font-serif-display text-3xl md:text-4xl tracking-tight font-medium mb-4">
                Free for candidates
              </h2>
              <p className="text-sm md:text-base leading-relaxed text-[#d7eadc]">
                Candidates can create a job-search profile and prepare application materials for free. The candidate experience stays focused on clarity, preparation, and momentum.
              </p>
            </div>
            <div className="rounded-[2rem] border border-[#d8c8ab] bg-white/45 p-8 md:p-10" data-testid="europe-focus-card">
              <Globe2 className="w-8 h-8 mb-6 text-[#173d35]" />
              <h2 className="font-serif-display text-3xl md:text-4xl tracking-tight font-medium mb-4 text-[#172820]">
                Europe-first search focus
              </h2>
              <p className="text-sm md:text-base leading-relaxed text-[#6f6657] mb-6">
                Germany and London/UK are launch markets. Remote Europe is supported, with the search shaped around your target direction.
              </p>
              <div className="flex flex-wrap gap-2">
                {markets.map((market) => (
                  <span key={market} className="inline-flex items-center gap-2 rounded-full border border-[#d7c5a7] bg-[#fff9ee] px-4 py-2 text-sm text-[#30473e]">
                    <MapPin className="w-3.5 h-3.5" /> {market}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-14 md:py-24" data-testid="cta-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="rounded-[2rem] border border-[#d8c8ab] bg-[#fff9ee]/80 p-8 md:p-14 text-center shadow-sm">
            <div className="mx-auto mb-6 flex h-14 w-14 items-center justify-center rounded-full bg-[#173d35] text-[#fff8ed]">
              <Layers className="w-6 h-6" />
            </div>
            <h2 className="font-serif-display text-3xl md:text-4xl tracking-tight font-medium mb-4 text-[#172820]">
              Start with a clear search profile
            </h2>
            <p className="text-[#6f6657] mb-8 max-w-xl mx-auto text-sm md:text-base leading-relaxed">
              Start with your direction, review matched roles, and prepare a customised application kit for selected roles.
            </p>
            <Link
              to="/find-jobs/check-fit"
              className="inline-flex items-center justify-center gap-2.5 rounded-md bg-[#173d35] px-6 py-4 text-sm font-medium text-[#fff8ed] shadow-sm transition-[transform,background-color] duration-300 hover:-translate-y-0.5 hover:bg-[#102f29]"
              data-testid="cta-build-career-agent-btn"
            >
              Start my job search
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
